import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  Business as BusinessIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';

const ACTION_TYPES = {
  ROLE_GRANTED: { label: 'Role Granted', color: 'success', icon: <SecurityIcon /> },
  ROLE_REVOKED: { label: 'Role Revoked', color: 'error', icon: <SecurityIcon /> },
  PERMISSION_GRANTED: { label: 'Permission Granted', color: 'success', icon: <BusinessIcon /> },
  PERMISSION_REVOKED: { label: 'Permission Revoked', color: 'error', icon: <BusinessIcon /> },
  INGESTION_CREATED: { label: 'Ingestion Created', color: 'info', icon: <CloudUploadIcon /> },
  INGESTION_MODIFIED: { label: 'Ingestion Modified', color: 'warning', icon: <CloudUploadIcon /> },
  INGESTION_DELETED: { label: 'Ingestion Deleted', color: 'error', icon: <CloudUploadIcon /> },
  USER_LOGIN: { label: 'User Login', color: 'default', icon: <PersonIcon /> },
  USER_LOGOUT: { label: 'User Logout', color: 'default', icon: <PersonIcon /> }
};

// eslint-disable-next-line max-lines-per-function
function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  const [users, setUsers] = useState([]);

  useEffect(() => {
    fetchAuditLogs();
    fetchUsers();
  }, []);

  const fetchAuditLogs = async () => {
    try {
      // TODO: Replace with actual API call
      const mockLogs = [
        {
          id: '1',
          timestamp: new Date().toISOString(),
          userId: '1',
          userEmail: 'jeremy.brooks@faanaworks.com',
          userName: 'Jeremy Brooks',
          action: 'ROLE_GRANTED',
          targetUserId: '2',
          targetUserEmail: 'jane.doe@faanaworks.com',
          targetUserName: 'Jane Doe',
          metadata: { role: 'financial' },
          ipAddress: '192.168.1.1'
        },
        {
          id: '2',
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          userId: '1',
          userEmail: 'jeremy.brooks@faanaworks.com',
          userName: 'Jeremy Brooks',
          action: 'PERMISSION_GRANTED',
          targetUserId: '2',
          targetUserEmail: 'jane.doe@faanaworks.com',
          targetUserName: 'Jane Doe',
          metadata: { entityId: 'entity_001', entityName: 'Sample Organization', permissions: ['financial', 'leadership'] },
          ipAddress: '192.168.1.1'
        },
        {
          id: '3',
          timestamp: new Date(Date.now() - 7200000).toISOString(),
          userId: '1',
          userEmail: 'jeremy.brooks@faanaworks.com',
          userName: 'Jeremy Brooks',
          action: 'INGESTION_CREATED',
          metadata: {
            ingestionName: 'Acme Corp Drive',
            type: 'google_drive',
            entityId: 'entity_001',
            entityName: 'Sample Organization'
          },
          ipAddress: '192.168.1.1'
        },
        {
          id: '4',
          timestamp: new Date(Date.now() - 10800000).toISOString(),
          userId: '2',
          userEmail: 'jane.doe@faanaworks.com',
          userName: 'Jane Doe',
          action: 'USER_LOGIN',
          ipAddress: '192.168.1.2'
        }
      ];

      setLogs(mockLogs);
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      // TODO: Replace with actual API call
      const mockUsers = [
        { uid: '1', email: 'jeremy.brooks@faanaworks.com', displayName: 'Jeremy Brooks' },
        { uid: '2', email: 'jane.doe@faanaworks.com', displayName: 'Jane Doe' },
        { uid: '3', email: 'john.smith@faanaworks.com', displayName: 'John Smith' }
      ];
      setUsers(mockUsers);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportLogs = () => {
    // TODO: Implement export functionality
    console.log('Exporting logs...');
  };

  const getActionDisplay = (log) => {
    const actionConfig = ACTION_TYPES[log.action];
    if (!actionConfig) return { label: log.action, color: 'default' };
    return actionConfig;
  };

  const formatLogMessage = (log) => {
    switch (log.action) {
      case 'ROLE_GRANTED':
        return `Granted ${log.metadata.role} role to ${log.targetUserName}`;
      case 'ROLE_REVOKED':
        return `Revoked ${log.metadata.role} role from ${log.targetUserName}`;
      case 'PERMISSION_GRANTED':
        return `Granted ${log.metadata.permissions.join(', ')} permissions to ${log.targetUserName} for ${log.metadata.clientName}`;
      case 'PERMISSION_REVOKED':
        return `Revoked permissions from ${log.targetUserName} for ${log.metadata.clientName}`;
      case 'INGESTION_CREATED':
        return `Created ${log.metadata.type} ingestion "${log.metadata.ingestionName}" for ${log.metadata.clientName}`;
      case 'INGESTION_MODIFIED':
        return `Modified ingestion "${log.metadata.ingestionName}"`;
      case 'INGESTION_DELETED':
        return `Deleted ingestion "${log.metadata.ingestionName}"`;
      case 'USER_LOGIN':
        return 'Logged in';
      case 'USER_LOGOUT':
        return 'Logged out';
      default:
        return log.action;
    }
  };

  // Filter logs
  const filteredLogs = logs.filter(log => {
    const matchesSearch = !searchTerm ||
      log.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      formatLogMessage(log).toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = filterType === 'all' || log.action === filterType;
    const matchesUser = filterUser === 'all' || log.userId === filterUser;

    return matchesSearch && matchesType && matchesUser;
  });

  // Paginate logs
  const paginatedLogs = filteredLogs.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">
          <TimeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Audit Log
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchAuditLogs}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportLogs}
          >
            Export
          </Button>
        </Box>
      </Box>
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid
            size={{
              xs: 12,
              sm: 4
            }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 4
            }}>
            <FormControl fullWidth>
              <InputLabel>Action Type</InputLabel>
              <Select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                label="Action Type"
              >
                <MenuItem value="all">All Actions</MenuItem>
                {Object.entries(ACTION_TYPES).map(([key, config]) => (
                  <MenuItem key={key} value={key}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {config.icon}
                      <Typography sx={{ ml: 1 }}>{config.label}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 4
            }}>
            <FormControl fullWidth>
              <InputLabel>User</InputLabel>
              <Select
                value={filterUser}
                onChange={(e) => setFilterUser(e.target.value)}
                label="User"
              >
                <MenuItem value="all">All Users</MenuItem>
                {users.map(user => (
                  <MenuItem key={user.uid} value={user.uid}>
                    {user.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>
      {/* Logs Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Timestamp</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Details</TableCell>
              <TableCell>IP Address</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedLogs.map((log) => {
              const actionDisplay = getActionDisplay(log);

              return (
                <TableRow key={log.id}>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(log.timestamp).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ width: 32, height: 32, mr: 1 }}>
                        {log.userName?.[0] || log.userEmail[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="body2">{log.userName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {log.userEmail}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={actionDisplay.label}
                      size="small"
                      color={actionDisplay.color}
                      icon={actionDisplay.icon}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatLogMessage(log)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {log.ipAddress}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredLogs.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
}

export default AuditLog;